import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { handleProductParticipationConfirmation } from '@adornis/buchungsheld-db/api/participation.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { catchError, combineLatest, filter, map, of, switchMap } from 'rxjs';

@customElement('bh-page-product-participated')
export class BHPageProductParticipated extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @state() private readonly _user = new RXController(this, currentUser<User>());
  @state() private readonly productID = new RXController(this, routingState.searchParams.get('productID'));
  @state() private readonly token = new RXController(this, routingState.searchParams.get('token'));

  @state() private readonly result = new RXController(
    this,
    combineLatest([this._user.observable, this.productID.observable, this.token.observable]).pipe(
      filter(([user, productID, token]) => !!user && !!productID && !!token), // only procceed when user is logged in
      switchMap(([_user, productID, token]) => {
        if (!productID || !token) throw new Error('unreachable');
        return handleProductParticipationConfirmation(productID, token)();
      }),
      map(result => {
        return { status: 'ok' as const, result };
      }),
      catchError(error => {
        const msg = error.message ?? 'unknown error';
        return of({ status: 'error' as const, msg });
      }),
    ),
  );

  @state() private readonly product = new RXController(
    this,
    this.productID.observable.pipe(
      filter(isDefined),
      switchMap(id => subscribeByID(Workshop, id)({ name: 1 })),
    ),
  );

  override render() {
    if (this.result.isLoading || this.product.isLoading) {
      return html` <x-loading-state></x-loading-state> `;
    }

    return html`
      <x-flex ${css({ height: '100vh' })} center crossaxis-center>
        <x-card ${css({ maxWidth: '600px' })}> ${this.content()} </x-card>
      </x-flex>
    `;
  }

  private content() {
    const result = this.result.value;
    if (!result) throw new Error('unreachable');

    if (result.status === 'error')
      return html`
        <x-flex space="md" crossaxis-center padding="lg">
          <x-icon ${css({ color: this.colors.tone.error, fontSize: '3em' })}> block </x-icon>
          <x-text> ${this._translation.translate('participated', 'error', result.msg)} </x-text>
        </x-flex>
      `;

    return html`
      <x-flex space="md" crossaxis-center padding="lg">
        <x-icon ${css({ color: this.colors.tone.success, fontSize: '3em' })}> verified </x-icon>
        <x-text align="center">
          ${this._translation.translate('participated', 'successfull_participation')} '${this.product.value?.name}'
        </x-text>

        <x-button @click=${() => goTo('/profile')}>
          ${this._translation.translate('REGISTRATION', 'to_profile')}
        </x-button>
      </x-flex>
    `;
  }
}
